import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../App';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, ShieldCheck, X, CalendarClock, AlertTriangle, BadgePoundSterling } from 'lucide-react';
import { formatDuration, formatAddress } from '../utils/format';
import { getPolicy } from '../utils/policies';

const STATUS_STYLE = {
  confirmed: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  pending_payment: 'bg-orange-100 text-orange-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
  reschedule_requested: 'bg-purple-100 text-purple-700',
};

export default function GuestBookingManage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [refundPreview, setRefundPreview] = useState(null);

  const fetchBooking = useCallback(async () => {
    if (!token) { setError('No access token provided.'); setLoading(false); return; }
    try {
      const res = await api.get(`/bookings/guest/manage?token=${token}`);
      setBooking(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to load booking. This link may have expired.');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchBooking(); }, [fetchBooking]);

  const fetchRefundPreview = async () => {
    if (!booking || booking.payment_status !== 'paid') return;
    try {
      const res = await api.get(`/bookings/${booking.booking_id}/refund-preview`);
      setRefundPreview(res.data);
    } catch (err) { console.error('Refund preview failed:', err); }
  };

  useEffect(() => {
    if (cancelOpen && booking) fetchRefundPreview();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelOpen]);

  const handleCancel = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/bookings/guest/cancel?token=${token}`, { reason: cancelReason });
      const refund = res.data?.refund;
      if (refund?.eligible && refund?.refund_amount > 0) {
        toast.success(`Booking cancelled. £${refund.refund_amount.toFixed(2)} refund issued`);
      } else {
        toast.success('Booking cancelled');
      }
      setCancelOpen(false);
      fetchBooking();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally { setSubmitting(false); }
  };

  const handleReschedule = async () => {
    if (!newDate || !newTime) { toast.error('Please select a new date and time'); return; }
    setSubmitting(true);
    try {
      await api.post(`/bookings/guest/reschedule?token=${token}`, { date: newDate, time: newTime });
      toast.success('Reschedule request sent to provider');
      setRescheduleOpen(false);
      fetchBooking();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request reschedule');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="flex justify-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#D4AF37]" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-lg mx-auto px-6 py-16 text-center">
          <div className="card-surface p-8">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-2">Unable to Load Booking</h2>
            <p className="text-stone-500">{error}</p>
            <p className="text-sm text-stone-400 mt-4">If you need help, contact <a href="mailto:support@zuriya.co.uk" className="text-[#D4AF37] underline">support@zuriya.co.uk</a></p>
          </div>
        </div>
      </div>
    );
  }

  const ref = booking.booking_ref || booking.booking_id;
  const isCancelled = booking.status === 'cancelled';
  const isCompleted = booking.status === 'completed';
  const canManage = !isCancelled && !isCompleted && booking.status !== 'pending_payment';
  const isRescheduleRequested = booking.status === 'reschedule_requested';
  const policy = booking.cancellation_policy;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="max-w-lg mx-auto px-6 py-8">
        <div className="card-surface p-8" data-testid="guest-booking-manage">
          <div className="flex items-center justify-between mb-6">
            <h1 className="font-heading text-2xl font-medium text-[#1A1A1A]">Your Booking</h1>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_STYLE[booking.status] || 'bg-stone-100 text-stone-600'}`} data-testid="guest-booking-status">
              {booking.status === 'reschedule_requested' ? 'Reschedule Requested' : booking.status}
            </span>
          </div>

          {/* Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-stone-400 w-24 shrink-0">Reference</span>
              <span className="font-mono font-bold text-[#D4AF37]" data-testid="guest-booking-ref">{ref}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-stone-400 w-24 shrink-0">Service</span>
              <span className="font-medium text-[#1A1A1A]">{booking.service_name}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-stone-400 w-24 shrink-0">Provider</span>
              <span className="text-[#1A1A1A]">{booking.provider_name || booking.provider_brand_name}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Calendar className="h-4 w-4 text-stone-400" />
              <span className="text-[#1A1A1A]">{booking.date} at {booking.time}</span>
            </div>
            {booking.duration && (
              <div className="flex items-center gap-3 text-sm">
                <Clock className="h-4 w-4 text-stone-400" />
                <span className="text-[#1A1A1A]">{formatDuration(booking.duration)}</span>
              </div>
            )}
          </div>

          {/* Location */}
          {booking.provider_service_address && (
            <div className="mt-4 p-3 bg-[#F9F6F0] rounded-lg flex items-start gap-2">
              <MapPin className="h-4 w-4 text-[#D4AF37] mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-stone-400">Service address</p>
                <p className="text-sm text-[#1A1A1A] font-medium">{formatAddress(booking.provider_service_address)}</p>
              </div>
            </div>
          )}
          {booking.service_location === 'mobile' && booking.client_address && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <MapPin className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-amber-600">Provider will come to</p>
                <p className="text-sm text-[#1A1A1A] font-medium">{formatAddress(booking.client_address)}</p>
              </div>
            </div>
          )}

          {/* Payment */}
          <div className="mt-6 pt-4 border-t border-[#E6E4DD] flex justify-between items-center">
            <span className="text-stone-500 text-sm">Total paid</span>
            <span className="text-xl font-bold text-[#D4AF37]">£{booking.price?.toFixed(2)}</span>
          </div>

          {/* Cancellation policy */}
          {policy && (
            <div className="mt-4 p-3 bg-stone-50 rounded-lg flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 text-stone-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-stone-400">{getPolicy(policy.type).label}</p>
                <p className="text-sm text-stone-600">{getPolicy(policy.type).summary}</p>
              </div>
            </div>
          )}

          {/* Refund info on cancelled bookings */}
          {isCancelled && booking.refund && (
            <div className={`mt-4 p-3 rounded-lg ${booking.refund.amount > 0 ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
              <p className={`text-sm font-medium ${booking.refund.amount > 0 ? 'text-green-700' : 'text-amber-700'}`}>
                {booking.refund.amount > 0 ? `£${booking.refund.amount.toFixed(2)} refunded` : 'No refund'}
              </p>
              <p className={`text-xs ${booking.refund.amount > 0 ? 'text-green-600' : 'text-amber-600'}`}>{booking.refund.reason}</p>
            </div>
          )}

          {/* Reschedule pending */}
          {isRescheduleRequested && booking.reschedule_request && (
            <div className="mt-4 p-3 bg-purple-50 rounded-lg text-sm text-purple-700">
              Reschedule requested: {booking.reschedule_request.new_date} at {booking.reschedule_request.new_time}, awaiting provider response
            </div>
          )}

          {/* Actions */}
          {canManage && !isRescheduleRequested && (
            <div className="mt-6 flex gap-3">
              <Button variant="outline" className="flex-1 gap-1 border-stone-200 text-stone-500 hover:text-red-600 hover:border-red-200" onClick={() => setCancelOpen(true)} data-testid="guest-cancel-btn">
                <X className="h-4 w-4" />Cancel Booking
              </Button>
              <Button variant="outline" className="flex-1 gap-1 border-[#D4AF37] text-[#D4AF37] hover:bg-amber-50" onClick={() => setRescheduleOpen(true)} data-testid="guest-reschedule-btn">
                <CalendarClock className="h-4 w-4" />Reschedule
              </Button>
            </div>
          )}

          <p className="text-center text-xs text-stone-400 mt-6">
            Need help? <a href="mailto:support@zuriya.co.uk" className="text-[#D4AF37] underline">support@zuriya.co.uk</a>
          </p>
        </div>
      </div>

      {/* Cancel Dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Cancel Booking</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-stone-600">Are you sure you want to cancel <strong>{booking?.service_name}</strong> on {booking?.date}?</p>
            {booking?.payment_status === 'paid' && refundPreview && (
              <div className={`p-3 rounded-lg text-sm ${refundPreview.eligible ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`} data-testid="guest-refund-preview">
                <div className="flex items-center gap-2 mb-1">
                  {refundPreview.eligible ? <BadgePoundSterling className="h-4 w-4 text-green-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
                  <span className={`font-medium ${refundPreview.eligible ? 'text-green-700' : 'text-amber-700'}`}>
                    {refundPreview.eligible ? `£${refundPreview.refund_amount.toFixed(2)} refund` : 'No refund'}
                  </span>
                </div>
                <p className={`text-xs ${refundPreview.eligible ? 'text-green-600' : 'text-amber-600'}`}>{refundPreview.reason}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Reason (optional)</label>
              <Textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Let the provider know why..." className="input-zuriya" data-testid="guest-cancel-reason" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setCancelOpen(false)}>Keep Booking</Button>
              <Button className="bg-red-500 hover:bg-red-600 text-white" onClick={handleCancel} disabled={submitting} data-testid="guest-confirm-cancel">
                {submitting ? 'Cancelling...' : 'Cancel Booking'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Request Reschedule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-stone-600">Choose a new date and time. The provider will need to approve the change.</p>
            <div>
              <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">New Date</label>
              <Input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} className="input-zuriya" min={new Date().toISOString().split('T')[0]} data-testid="guest-reschedule-date" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">New Time</label>
              <Input type="time" value={newTime} onChange={e => setNewTime(e.target.value)} className="input-zuriya" data-testid="guest-reschedule-time" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setRescheduleOpen(false)}>Cancel</Button>
              <Button className="btn-primary" onClick={handleReschedule} disabled={submitting} data-testid="guest-confirm-reschedule">
                {submitting ? 'Requesting...' : 'Request Reschedule'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
